/**
 * Auth context: currentUser.role for RBAC.
 * Token va user localStorage da saqlanadi — sahifa yangilanganda sessiya saqlanadi.
 */

import React, { createContext, useContext, useMemo, useState, useEffect, useCallback, ReactNode } from 'react';
import type { Role } from '../config/roles';
import { getRoleFromRoleId } from '../config/roles';
import type { PlanCapabilitiesSnapshot } from '../types/planCapabilities';
import {
  getProfile,
  getStoredToken,
  clearStoredToken,
  logoutApi,
  getStoredSessionId,
} from '../services/authApi';
import * as adminImpersonationStorage from '../utils/adminImpersonationStorage';
import { disconnectEcho, initEcho } from '../services/echoClient';

/**
 * Kirish holati: access token (yoki legacy auth_token) mavjudligi.
 * adminUser JSON i ba'zan token bilan sinxron emas (rol almashtirish, qisman tozalangan storage);
 * shunda ham sessiya token bo'yicha amal qiladi — getProfile to'ldiradi.
 */
export function hasValidAuth() {
  return !!getStoredToken();
}

export interface User {
  id: string;
  name: string;
  phone?: string;
  role: Role;
  /** Backend `province_id` — viloyat menejer statistikasi uchun */
  provinceId?: string;
  /** API `region_id` */
  regionId?: string;
  /** Viloyat nomi (masalan regions.name_uz) */
  region?: string;
  /** Mijoz uchun: bog'langan tenant (kompaniya) ID */
  tenantId?: string;
  /** Kassir uchun: bog'langan filial */
  branchId?: string;
  branchName?: string;
  /** Rol almashtirish PIN holati (backenddan) */
  roleSwitchPinSet?: boolean;
  mustSetRoleSwitchPin?: boolean;
  /** Kirish paroli o'rnatilganligi (backenddan) */
  loginPasswordSet?: boolean;
  /** Kassir: mahsulot qo'shish/tahrirlash/o'chirish */
  cashierCanManageProducts?: boolean;
  cashierCanAddProductsOnly?: boolean;
  /** Kassir: mavjud mahsulotga shtrix-kod qo'shish */
  cashierCanAddProductBarcodes?: boolean;
  /** Kassir: faqat POS kabina (boshqa bo'limlar yopiq) */
  cashierPosOnly?: boolean;
  /** GET /api/user — tarif limitlari va modullar */
  planCapabilities?: PlanCapabilitiesSnapshot | null;
}

interface AuthContextValue {
  user: User | null;
  /** Asl foydalanuvchi (API/login), faqat tenant_owner/cashier uchun rol almashtirishda ishlatiladi */
  baseUser: User | null;
  isAuthenticated: boolean;
  logout: () => void;
  setUser: (user: User | null) => void;
  setUserFromRole: (role: Role) => void;
  /** Mijoz/cashier: efektiv rol (va ixtiyoriy cashier id). null = asl rol. */
  setEffectiveRole: (role: Role | null, asUserId?: string | null) => void;
  authLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = 'adminRole';
const USER_STORAGE_KEY = 'adminUser';
const EFFECTIVE_ROLE_KEY = 'effectiveRoleSwitch';
const EFFECTIVE_AS_USER_ID_KEY = 'effectiveAsUserIdSwitch';

function readPersisted(key: string): string | null {
  try {
    return window.localStorage?.getItem?.(key) ?? window.sessionStorage?.getItem?.(key) ?? null;
  } catch {
    return null;
  }
}

function writePersisted(key: string, value: string | null) {
  try {
    if (value == null) {
      window.localStorage?.removeItem?.(key);
      window.sessionStorage?.removeItem?.(key);
      return;
    }
    window.localStorage?.setItem?.(key, value);
    window.sessionStorage?.setItem?.(key, value);
  } catch {}
}

function getStoredEffective(): { role: Role | null; asUserId: string | null } {
  try {
    const r = readPersisted(EFFECTIVE_ROLE_KEY);
    const id = readPersisted(EFFECTIVE_AS_USER_ID_KEY);
    if (!r) return { role: null, asUserId: null };
    return { role: (LEGACY_ROLE_MAP[r] ?? r) as Role, asUserId: id || null };
  } catch {
    return { role: null, asUserId: null };
  }
}

function setStoredEffective(role: Role | null, asUserId: string | null) {
  try {
    if (role) {
      writePersisted(EFFECTIVE_ROLE_KEY, role);
      writePersisted(EFFECTIVE_AS_USER_ID_KEY, asUserId ?? '');
    } else {
      writePersisted(EFFECTIVE_ROLE_KEY, null);
      writePersisted(EFFECTIVE_AS_USER_ID_KEY, null);
    }
  } catch {}
}

/** Eski sessionStorage kalitlarini yangi Role ga map qilish */
export const LEGACY_ROLE_MAP: Record<string, Role> = {
  platform_owner: 'platform_owner',
  general_manager: 'general_manager',
  regional_manager: 'regional_manager',
  dispatcher: 'dispatcher',
  tenant_owner: 'tenant_owner',
  cashier: 'cashier',
  ega: 'platform_owner',
  bosh_menejer: 'general_manager',
  viloyat_menejer: 'regional_manager',
  dispecher: 'dispatcher',
  mijoz: 'tenant_owner',
  kassir: 'cashier',
  super_admin: 'platform_owner',
  biznes_admin: 'regional_manager',
  menejer_sotuvchi: 'regional_manager',
  kuryer: 'tenant_owner',
  kuryer_field: 'tenant_owner',
  customer: 'tenant_owner',
};

export function mapLegacyRoleToRole(legacy: string): Role {
  return LEGACY_ROLE_MAP[legacy] ?? (legacy as Role);
}

export function normalizeUser(profile: {
  id?: number | string;
  phone?: string;
  name?: string;
  role?: string;
  role_id?: number;
  region_id?: number | string;
  region?: string;
  province_id?: number | string | null;
  tenant_id?: number | string;
  branch_id?: number | string;
  branch_name?: string;
  branch?: { id?: number | string; name?: string } | null;
  role_switch_pin_set?: boolean;
  must_set_role_switch_pin?: boolean;
  login_password_set?: boolean;
  cashier_can_manage_products?: boolean;
  cashier_can_add_barcodes?: boolean;
  plan_capabilities?: PlanCapabilitiesSnapshot | null;
  planCapabilities?: PlanCapabilitiesSnapshot | null;
}): User {
  const roleCandidate =
    (profile as any)?.role_id ??
    (profile as any)?.roleId ??
    (profile as any)?.role ??
    (profile as any)?.role_name ??
    (profile as any)?.roleName ??
    (profile as any)?.user_type ??
    (profile as any)?.userType ??
    (profile as any)?.type ??
    null;
  let role = getRoleFromRoleId(roleCandidate ?? 'platform_owner');
  const branchIdRaw = (profile as any).branch_id ?? (profile as any).branchId ?? (profile as any).branch?.id;
  const branchNameRaw = (profile as any).branch_name ?? (profile as any).branchName ?? (profile as any).branch?.name;
  const cashierIdRaw = (profile as any).cashier_id ?? (profile as any).cashierId;
  const isCashierFlag = (profile as any).is_cashier ?? (profile as any).isCashier;
  if ((roleCandidate == null || String(roleCandidate).trim() === '') && (cashierIdRaw != null || isCashierFlag === true)) {
    role = 'cashier';
  }
  const regTrim =
    profile?.region != null && String(profile.region).trim() !== '' ? String(profile.region).trim() : undefined;
  const provinceId =
    profile?.province_id != null && String(profile.province_id) !== '' ? String(profile.province_id) : undefined;
  const planCaps =
    (profile as { plan_capabilities?: PlanCapabilitiesSnapshot }).plan_capabilities ??
    (profile as { planCapabilities?: PlanCapabilitiesSnapshot }).planCapabilities ??
    null;
  return {
    id: String(profile?.id ?? ''),
    name: profile?.name ?? profile?.phone ?? 'User',
    phone: profile?.phone ? String(profile.phone) : undefined,
    role,
    provinceId,
    regionId: profile?.region_id != null ? String(profile.region_id) : undefined,
    region: regTrim ?? profile?.region ?? undefined,
    tenantId: profile?.tenant_id != null ? String(profile.tenant_id) : undefined,
    branchId: branchIdRaw != null ? String(branchIdRaw) : undefined,
    branchName: branchNameRaw != null ? String(branchNameRaw) : undefined,
    roleSwitchPinSet: (profile as any).role_switch_pin_set ?? (profile as any).roleSwitchPinSet,
    mustSetRoleSwitchPin: (profile as any).must_set_role_switch_pin ?? (profile as any).mustSetRoleSwitchPin,
    loginPasswordSet: (profile as any).login_password_set ?? (profile as any).loginPasswordSet,
    cashierCanManageProducts: (profile as any).cashier_can_manage_products ?? (profile as any).cashierCanManageProducts,
    cashierCanAddProductBarcodes: (profile as any).cashier_can_add_barcodes ?? (profile as any).cashierCanAddBarcodes,
    cashierCanAddProductsOnly: (profile as any).cashier_can_add_products_only ?? (profile as any).cashierCanAddProductsOnly,
    cashierPosOnly: (profile as any).cashier_pos_only ?? (profile as any).cashierPosOnly,
    planCapabilities: planCaps && typeof planCaps === 'object' ? planCaps : null,
  };
}

function getStoredRole(): Role | null {
  try {
    const raw = readPersisted(STORAGE_KEY);
    if (!raw) return null;
    return LEGACY_ROLE_MAP[raw] ?? (raw as Role);
  } catch {
    return null;
  }
}

function getStoredUser(): User | null {
  try {
    const raw = window.localStorage?.getItem?.(USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.role) return null;
    const tid = parsed.tenantId ?? parsed.tenant_id;
    const rid = parsed.regionId ?? parsed.region_id;
    return {
      id: String(parsed.id ?? ''),
      name: String(parsed.name ?? 'User'),
      phone: parsed.phone != null ? String(parsed.phone) : undefined,
      role: LEGACY_ROLE_MAP[parsed.role] ?? (parsed.role as Role),
      provinceId: parsed.provinceId != null && String(parsed.provinceId) !== '' ? String(parsed.provinceId) : undefined,
      regionId: rid != null && rid !== '' ? String(rid) : undefined,
      region: typeof parsed.region === 'string' && parsed.region.trim() ? parsed.region.trim() : parsed.region,
      tenantId: tid != null && tid !== '' ? String(tid) : undefined,
      branchId: parsed.branchId ?? parsed.branch_id,
      branchName: parsed.branchName ?? parsed.branch_name,
      roleSwitchPinSet: parsed.roleSwitchPinSet ?? parsed.role_switch_pin_set,
      loginPasswordSet: parsed.loginPasswordSet ?? parsed.login_password_set,
      cashierCanManageProducts: parsed.cashierCanManageProducts ?? parsed.cashier_can_manage_products,
      cashierCanAddProductBarcodes: parsed.cashierCanAddProductBarcodes ?? parsed.cashier_can_add_barcodes,
      cashierCanAddProductsOnly: parsed.cashierCanAddProductsOnly ?? parsed.cashier_can_add_products_only,
      cashierPosOnly: parsed.cashierPosOnly ?? parsed.cashier_pos_only,
      planCapabilities: parsed.planCapabilities ?? parsed.plan_capabilities ?? null,
    };
  } catch {
    return null;
  }
}

function setStoredUser(u: User | null) {
  try {
    if (u) window.localStorage?.setItem?.(USER_STORAGE_KEY, JSON.stringify(u));
    else window.localStorage?.removeItem?.(USER_STORAGE_KEY);
  } catch {}
}

/** Birinchi paint: localStorage bo'yicha sinxron — landingda "Kirish" flash bo'lmasin. */
function readInitialAuthState(): {
  baseUser: User | null;
  effectiveRole: Role | null;
  effectiveAsUserId: string | null;
} {
  if (typeof window === 'undefined') {
    return { baseUser: null, effectiveRole: null, effectiveAsUserId: null };
  }
  const token = getStoredToken();
  const storedUser = getStoredUser();
  const storedRole = getStoredRole();

  if (!token) {
    return { baseUser: null, effectiveRole: null, effectiveAsUserId: null };
  }

  const initialUser = storedUser ?? { id: '1', name: 'Admin User', role: storedRole ?? 'platform_owner' };
  const bu = storedRole ? { ...initialUser, role: storedRole } : initialUser;
  const eff = getStoredEffective();
  if (eff.role && (bu.role === 'tenant_owner' || bu.role === 'cashier')) {
    return { baseUser: bu, effectiveRole: eff.role, effectiveAsUserId: eff.asUserId };
  }
  return { baseUser: bu, effectiveRole: null, effectiveAsUserId: null };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [baseUser, setBaseUserState] = useState<User | null>(() => readInitialAuthState().baseUser);
  const [effectiveRole, setEffectiveRoleState] = useState<Role | null>(() => readInitialAuthState().effectiveRole);
  const [effectiveAsUserId, setEffectiveAsUserIdState] = useState<string | null>(() => readInitialAuthState().effectiveAsUserId);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const token = getStoredToken();

    if (!token) {
      try {
        setStoredUser(null);
        writePersisted(STORAGE_KEY, null);
        writePersisted(EFFECTIVE_ROLE_KEY, null);
        writePersisted(EFFECTIVE_AS_USER_ID_KEY, null);
      } catch {}
      setBaseUserState(null);
      setEffectiveRoleState(null);
      setEffectiveAsUserIdState(null);
      setAuthLoading(false);
      return;
    }

    getProfile()
      .then((profile) => {
        const u = normalizeUser(profile as Parameters<typeof normalizeUser>[0]);
        setBaseUserState(u);
        setStoredUser(u);
        try {
          if (u.role) writePersisted(STORAGE_KEY, u.role);
        } catch {}
        const eff2 = getStoredEffective();
        if (eff2.role && (u.role === 'tenant_owner' || u.role === 'cashier')) {
          setEffectiveRoleState(eff2.role);
          setEffectiveAsUserIdState(eff2.asUserId);
        }
      })
      .catch((err: unknown) => {
        const status = err && typeof err === 'object' && 'status' in err ? (err as { status?: number }).status : undefined;
        const msg = err instanceof Error ? err.message : '';
        if (status === 401 || /unauthenticated/i.test(String(msg))) {
          try {
            clearStoredToken();
            setStoredUser(null);
            writePersisted(STORAGE_KEY, null);
            writePersisted(EFFECTIVE_ROLE_KEY, null);
            writePersisted(EFFECTIVE_AS_USER_ID_KEY, null);
          } catch {}
          setBaseUserState(null);
          setEffectiveRoleState(null);
          setEffectiveAsUserIdState(null);
        }
      })
      .finally(() => setAuthLoading(false));
  }, []);

  /** Maslahat session id mavjud bo‘lsa Echo ni oldindan yuklash (keyin private kanal nomi bilan kengaytirish mumkin). */
  useEffect(() => {
    if (!baseUser?.id || !getStoredToken()) return;
    if (!getStoredSessionId()) return;
    initEcho();
  }, [baseUser?.id]);

  const setUser = (u: User | null) => {
    setBaseUserState(u);
    setStoredUser(u);
    try {
      if (u?.role) writePersisted(STORAGE_KEY, u.role);
      else writePersisted(STORAGE_KEY, null);
    } catch {}
  };

  const setEffectiveRole = useCallback((role: Role | null, asUserId?: string | null) => {
    setEffectiveRoleState(role);
    setEffectiveAsUserIdState(role ? (asUserId ?? null) : null);
    setStoredEffective(role, role ? (asUserId ?? null) : null);
  }, []);

  const user = useMemo(() => {
    if (!baseUser) return null;
    if (effectiveRole && (baseUser.role === 'tenant_owner' || baseUser.role === 'cashier')) {
      return { ...baseUser, role: effectiveRole, id: effectiveAsUserId || baseUser.id };
    }
    return baseUser;
  }, [baseUser, effectiveRole, effectiveAsUserId]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      baseUser,
      isAuthenticated: Boolean(user && hasValidAuth()),
      logout: () => {
        disconnectEcho();
        adminImpersonationStorage.clearAdminImpersonationBackup();
        clearStoredToken();
        setStoredUser(null);
        try {
          writePersisted(STORAGE_KEY, null);
          writePersisted(EFFECTIVE_ROLE_KEY, null);
          writePersisted(EFFECTIVE_AS_USER_ID_KEY, null);
        } catch {}
        setBaseUserState(null);
        setEffectiveRoleState(null);
        setEffectiveAsUserIdState(null);
        logoutApi();
      },
      setUser,
      setUserFromRole: (role: Role) => {
        const u = { id: '1', name: 'Admin User', phone: undefined, role };
        setBaseUserState(u);
        setStoredUser(u);
        try {
          writePersisted(STORAGE_KEY, role);
        } catch {}
      },
      setEffectiveRole,
      authLoading,
    }),
    [user, baseUser, authLoading, setEffectiveRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
